/**
 * AI Storybook — small pure helpers shared by the storybook pages, the
 * builder and the landing section. No React in here (keeps fast-refresh happy
 * for the component files).
 */

import type { StorybookLanguage, StorybookOption, StorybookPricePoint } from '@/lib/storybook-api';

/** 'light-medium' -> 'light_medium', 'deep brown' -> 'deep_brown'. */
export function appearanceSlug(value: string): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
}

export type StorybookAgeBand = '0-3' | '4-6' | '7-9' | '10-12';

/** The ONLY age information analytics may ever see. */
export function storybookAgeBand(age: number): StorybookAgeBand {
  if (age <= 3) return '0-3';
  if (age <= 6) return '4-6';
  if (age <= 9) return '7-9';
  return '10-12';
}

/** 'es', 'es-MX', 'es-419' -> 'es'; everything else -> 'en'. */
export function storybookUiLanguage(language: string | undefined | null): StorybookLanguage {
  return String(language || '').toLowerCase().startsWith('es') ? 'es' : 'en';
}

/** Localized label from a config option ({id, en, es}). */
export function storybookOptionLabel(option: StorybookOption, language: string | undefined | null): string {
  const lang = storybookUiLanguage(language);
  return (lang === 'es' ? option.es : option.en) || option.en || option.id;
}

// CURRENCY RULE (owner-mandated, same as src/pages/Pricing.tsx): what Stripe
// charges is MXN; the USD figure is a display anchor, never charged. Both are
// shown at the same weight, formatted exactly the way the Pricing page does.
export const formatStorybookMxn = (cents: number) =>
  `$${Math.round(cents / 100).toLocaleString('en-US')}`;

export const formatStorybookUsd = (cents: number) => {
  const whole = cents % 100 === 0;
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
};

/** Both currencies, formatted the way every storybook price is shown. */
export function formatStorybookPrice(price: StorybookPricePoint): { mxn: string; usd: string } {
  return { mxn: formatStorybookMxn(price.cents), usd: formatStorybookUsd(price.usdCents) };
}

/**
 * What a printed book costs once Stripe Checkout adds its shipping line:
 * the book (or upgrade) plus shipping, both from /config, summed in cents
 * before formatting. null when shipping is free (the price is the total).
 */
export function storybookTotalWithShipping(
  price: StorybookPricePoint,
  shipping: StorybookPricePoint | null | undefined,
): { mxn: string; usd: string } | null {
  if (!shipping || !(shipping.cents > 0)) return null;
  return formatStorybookPrice({
    cents: price.cents + shipping.cents,
    usdCents: price.usdCents + (shipping.usdCents || 0),
  });
}

/** ['US', 'CA'] -> 'United States and Canada' in the UI language. */
export function formatCountryList(codes: string[], language: string | undefined | null): string {
  const lang = storybookUiLanguage(language);
  const list = (codes || []).filter(Boolean);
  let names = list;
  try {
    const display = new Intl.DisplayNames([lang], { type: 'region' });
    names = list.map((code) => display.of(code) || code);
  } catch {
    /* old browser: show the codes */
  }
  return formatStorybookList(names, lang);
}

/** ['Ana', 'Leo'] -> 'Ana and Leo' ('Ana y Leo') in the UI language. */
export function formatStorybookList(items: string[], language: string | undefined | null): string {
  const lang = storybookUiLanguage(language);
  const names = (items || []).filter(Boolean);
  // Intl.ListFormat is ES2021; typed loosely so the ES2020 lib still compiles.
  const ListFormat = (Intl as unknown as {
    ListFormat?: new (locale: string, options: Record<string, string>) => { format: (items: string[]) => string };
  }).ListFormat;
  try {
    if (ListFormat) return new ListFormat(lang, { style: 'long', type: 'conjunction' }).format(names);
  } catch {
    /* fall through */
  }
  return names.join(', ');
}

// ---------------------------------------------------------------------------
// Swatches for the appearance chips (decorative only; the value sent is the
// English id from GET /config).
// ---------------------------------------------------------------------------

export const SKIN_TONE_SWATCHES: Record<string, string> = {
  very_light: '#f7dfcc',
  light: '#eec6a8',
  light_medium: '#dcaa84',
  medium: '#c48d62',
  tan: '#a87243',
  brown: '#7b4c2c',
  deep_brown: '#4b2d1b',
};

export const HAIR_COLOR_SWATCHES: Record<string, string> = {
  black: '#1f1b1a',
  dark_brown: '#3b2417',
  brown: '#6a4028',
  light_brown: '#9a6a43',
  blonde: '#e6c574',
  strawberry_blonde: '#e3a36b',
  red: '#b5452b',
  auburn: '#8d3a22',
  gray: '#9d9a96',
  white: '#ece8e1',
};

/** The hair style that has no hair colour: the builder, the doodle and the server's prompt all ignore the colour. */
export const BALD_HAIR_STYLE = 'bald';

export function isBaldHairStyle(value: string | null | undefined): boolean {
  return appearanceSlug(value || '') === BALD_HAIR_STYLE;
}

export const EYE_COLOR_SWATCHES: Record<string, string> = {
  dark_brown: '#3a2215',
  brown: '#6b3f22',
  hazel: '#8e7440',
  green: '#4f8a4b',
  blue: '#4a86c5',
  gray: '#8a949c',
};
